/*
 * <ws-footer-tabs> — footer blocks that collapse into tabs on mobile
 * ---------------------------------------------------------------------------
 * Store customization for the Prestige theme (olivia luhman).
 *
 * Each footer block whose `ws_collapse_on_mobile` setting is on is rendered
 * (in sections/footer.liquid) as a native <details class="ws-footer-tab__details">
 * accordion. This component wraps the footer block list and manages the ONE
 * thing CSS cannot do portably: force those <details> fully open on desktop.
 *
 * Why JS is required:
 *   A closed <details> hides its content via content-visibility (Chromium) or
 *   the UA shadow slot (Firefox/Safari). Neither can be overridden with a
 *   `display` rule on the light-DOM children, so there is NO cross-browser
 *   CSS that force-opens a closed <details>. The only robust switch is the
 *   real `open` attribute — toggled here from a matchMedia query.
 *
 * Behavior:
 *   - >= 700px (desktop, matches the footer's own flex breakpoint):
 *       force every tab `open`, tag it [data-force-open] so CSS can make the
 *       summary inert, and render as a normal expanded column.
 *   - < 700px (mobile):
 *       restore the shopper's collapsed/expanded state and let the native
 *       <details> toggle work with zero JS involvement per interaction.
 *
 * Degradation: if this component never upgrades (JS off/failed), the tabs
 * remain working native <details> accordions on every screen size.
 *
 * Re-init: handled for free by the custom-element lifecycle — the Shopify
 * theme editor re-rendering the footer section destroys the old element
 * (disconnectedCallback) and upgrades the new one (connectedCallback).
 */
(function () {
  if (window.customElements.get('ws-footer-tabs')) return; // double-define guard

  var DESKTOP_QUERY = '(min-width: 700px)';

  class WsFooterTabs extends HTMLElement {
    constructor() {
      super();
      this._mql = null;
      this._onChange = this._sync.bind(this);
    }

    get tabs() {
      return Array.prototype.slice.call(
        this.querySelectorAll('.ws-footer-tab__details')
      );
    }

    connectedCallback() {
      if (!('matchMedia' in window)) return; // ancient browser: leave as native details
      this._mql = window.matchMedia(DESKTOP_QUERY);
      this._sync();

      // addEventListener('change') is the modern API; addListener is the
      // Safari < 14 fallback.
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._onChange);
      } else if (this._mql.addListener) {
        this._mql.addListener(this._onChange);
      }
    }

    disconnectedCallback() {
      if (!this._mql) return;
      if (this._mql.removeEventListener) {
        this._mql.removeEventListener('change', this._onChange);
      } else if (this._mql.removeListener) {
        this._mql.removeListener(this._onChange);
      }
      this._mql = null;
    }

    _sync() {
      var isDesktop = this._mql ? this._mql.matches : false;

      this.tabs.forEach(function (details) {
        if (isDesktop) {
          // Remember the shopper's mobile state exactly once, so toggling the
          // viewport back and forth never loses it.
          if (!details.hasAttribute('data-force-open')) {
            details.dataset.wasOpen = details.open ? '1' : '0';
          }
          details.open = true;
          details.setAttribute('data-force-open', '');
        } else {
          if (details.hasAttribute('data-force-open')) {
            details.removeAttribute('data-force-open');
            details.open = details.dataset.wasOpen === '1';
          }
        }
      });
    }
  }

  window.customElements.define('ws-footer-tabs', WsFooterTabs);
})();
